import { useGT } from 'gt-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  resolvePaymentStatusDisplay,
  type PaymentStatusDisplay,
} from './paymentStatusDisplay';

// Every entry sets its own hover colour (or uses the hover-free `outline`
// variant): the default variant's `hover:bg-primary/80` would otherwise bleed
// through and turn a quiet badge dark on mouse-over.
const BADGE_STYLES: Record<
  Exclude<PaymentStatusDisplay, 'unknown'>,
  { variant: 'default' | 'outline'; className: string }
> = {
  paid: {
    variant: 'default',
    className:
      'border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-600',
  },
  partially_paid: {
    variant: 'default',
    className:
      'border-amber-200 bg-amber-100 text-amber-800 hover:bg-amber-100',
  },
  overdue: {
    variant: 'default',
    className: 'border-red-700 bg-red-600 text-white hover:bg-red-600',
  },
  overpaid: {
    variant: 'default',
    className: 'border-red-200 bg-red-100 text-red-800 hover:bg-red-100',
  },
  unpaid: { variant: 'outline', className: '' },
};

/**
 * The one place an AP invoice's payment state is coloured, so the list, the
 * slide-in header and the payment fields never disagree. Paid is loud green;
 * an outstanding balance past its due date is loud red and reads "Overdue";
 * plain unpaid stays quiet because it is the queue's normal state.
 */
export function PaymentStatusBadge({
  status,
  dueDate,
  className,
}: {
  status: string;
  /** Lets an unpaid invoice past this date read as Overdue. */
  dueDate?: string | null;
  className?: string;
}) {
  const gt = useGT();
  const display = resolvePaymentStatusDisplay({ status, dueDate });

  if (display === 'unknown') {
    return (
      <Badge variant="outline" className={cn('capitalize', className)}>
        {status.replaceAll('_', ' ')}
      </Badge>
    );
  }

  const labels: Record<Exclude<PaymentStatusDisplay, 'unknown'>, string> = {
    paid: gt('Paid', { context: 'invoice payment status badge label' }),
    partially_paid: gt('Partially Paid', {
      context: 'invoice payment status badge label',
    }),
    unpaid: gt('Unpaid', { context: 'invoice payment status badge label' }),
    overpaid: gt('Overpaid', {
      context: 'invoice payment status badge label',
    }),
    overdue: gt('Overdue', {
      context:
        'invoice payment status badge label: unpaid and past the due date',
    }),
  };

  const style = BADGE_STYLES[display];
  return (
    <Badge variant={style.variant} className={cn(style.className, className)}>
      {labels[display]}
    </Badge>
  );
}
